import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import {
	AssignStatus,
	InclusionFilterStatus,
	PriorityFilterStatus,
	SignalFilterStatus,
} from "@/enum/list.enum";

import { IFilterWithValues } from "../../FilterModal";
import { getInclusionStatusFromUrl } from "../utils/urlFilterState";

export interface ListFilterUrlState {
	assignStatus?: AssignStatus;
	filter?: IFilterWithValues;
	searchTerm: string;
	inclusionStatus?: InclusionFilterStatus[];
	exclusionReasons: string[];
	priorityFilterStatus: PriorityFilterStatus[];
	signalFilterStatus: SignalFilterStatus[];
}

/** Sets `key` to the JSON of `value`, or removes it when `value` is the surface's default. */
const applyParam = (params: URLSearchParams, key: string, value: unknown, fallback: unknown) => {
	const encoded = JSON.stringify(value);

	if (encoded === undefined || encoded === JSON.stringify(fallback)) {
		params.delete(key);

		return;
	}

	params.set(key, encoded);
};

const hasFilterValues = (filter?: IFilterWithValues): boolean =>
	!!filter && Object.keys(filter).length > 0;

/**
 * Mirrors every lead-table filter into the query string, so a filtered view survives a reload and
 * can be shared/bookmarked (the table already *reads* all of these back on mount via
 * `utils/urlFilterState`).
 *
 * **One effect writes them all — deliberately.** This replaces four independent `useEffect`s that
 * each rebuilt the params from `location.search` and called `navigate()`. Mounting (or any commit
 * touching more than one filter) ran them in the same tick off the *same stale* search string, so
 * the last `navigate()` won and every other filter's write was silently dropped — which is why
 * picking a lead-scoring priority or a Show status never showed up in the URL. Writing all params in
 * a single pass removes the race; keying the effect on `searchParams` also self-heals a clobber by a
 * concurrent writer (e.g. `useSearch`'s page param), and the no-op guard keeps it from looping.
 *
 * Values equal to the surface's default are removed rather than serialized, so an untouched table
 * keeps a clean URL and only real selections show up.
 */
export const useListFilterUrlSync = ({
	assignStatus,
	filter,
	searchTerm,
	inclusionStatus,
	exclusionReasons,
	priorityFilterStatus,
	signalFilterStatus,
}: ListFilterUrlState): void => {
	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		const params = new URLSearchParams(searchParams);

		if (hasFilterValues(filter)) {
			params.set("filter", JSON.stringify(filter));
		} else {
			params.delete("filter");
		}

		if (searchTerm) {
			params.set("searchTerm", searchTerm);
		} else {
			params.delete("searchTerm");
		}

		applyParam(
			params,
			"inclusionStatus",
			inclusionStatus,
			getInclusionStatusFromUrl("", assignStatus)
		);
		applyParam(params, "exclusionReasons", exclusionReasons, []);
		applyParam(params, "priorityFilterStatus", priorityFilterStatus, [PriorityFilterStatus.ALL]);
		applyParam(params, "signalFilterStatus", signalFilterStatus, [SignalFilterStatus.ALL]);

		if (params.toString() !== searchParams.toString()) {
			setSearchParams(params, { replace: true });
		}
	}, [
		searchParams,
		setSearchParams,
		assignStatus,
		filter,
		searchTerm,
		inclusionStatus,
		exclusionReasons,
		priorityFilterStatus,
		signalFilterStatus,
	]);
};
